import React, { useState, useEffect, useCallback, useRef } from 'react';
import { captureMessage } from '@sentry/nextjs';
import { useRouter } from 'next/router';
import { Cache } from '@aws-amplify/cache';

import { LoadIcon } from '@svgs';

import { useUserContext } from '@context/user';

import { Container } from '@components/atoms/container';
import { Header } from '@components/atoms/header';
import { Link } from '@components/atoms/link';
import { Button } from '@components/atoms/button';

import { PlanCard } from '@components/molecules/planCard';
import { Loading } from '@components/molecules/loading';

import { Layout } from '@components/atoms/layout';

import { fireToast } from '@hooks/fireToast';
import { initiateStripeCheckout } from '@utilities/stripe';

import styles from './styles.module.scss';

const PlansPage = () => {
  const { user, isLoggedIn, plusMember, hasExpired } = useUserContext();
  const router = useRouter();
  const [resError, setResError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [checkUserIsPlus, setCheckIsUserPlus] = useState(false);
  const [notSSR, setNotSSR] = useState(false);
  const theToast = useRef();

  const { discount_code, interval } = router.query;

  const handlePlanSelection = useCallback(
    async (interval) => {
      if (isLoggedIn) {
        if (checkUserIsPlus) return router.push('/profile');

        theToast.current?.close && theToast.current.close();
        await initiateStripeCheckout({ setLoading, setResError, interval, router, promotionCode: discount_code });
      }
      else {
        Cache.setItem('plan_interval', interval);
        Cache.setItem('discount_code', discount_code);
        await router.push('/login');
      }
    },
    [checkUserIsPlus, isLoggedIn, router, discount_code]
  );

  useEffect(() => {
    (async () => {
      if (['month', 'sixmonth', 'year'].includes(interval)) {
        await handlePlanSelection(interval);
      } else if (router.isReady) { // Show plans once we know query params have loaded and plan isn't selected
        setLoading(false);
      }
    })();
  }, [interval, handlePlanSelection, router.isReady]);

  useEffect(() => {
    (async () => {
      if (isLoggedIn && (!plusMember || hasExpired) && user?.account) {

        const previouslySelectedInterval = Cache.getItem('plan_interval');
        const previouslySelectedDiscountCode = Cache.getItem('discount_code');
        if (!previouslySelectedInterval) {
          // Fire plus explanation toast
          const { matches } = window.matchMedia('(max-width: 1500px)');
          theToast.current = fireToast({
            type: 'info',
            message:
          'To log into Yepic on this device you need a Yepic Plus subscription. Choose a plan for access.',
            id: 'UserNotifiedNotOnPlan',
            position: matches ? 'TOP_CENTER' : 'BOTTOM_CENTER',
            autoClose: 99999999999,
            hideProgressBar: true,
            theme: 'dark'
          });
        }
        else {
          theToast.current?.close && theToast.current.close();
          Cache.removeItem('plan_interval');
          Cache.removeItem('discount_code');
          await initiateStripeCheckout({ 
            setLoading, 
            setResError, 
            interval: previouslySelectedInterval, 
            router, 
            profileId: user.account.profileId,
            promotionCode: previouslySelectedDiscountCode || discount_code 
          });
        }
      }
    })();
  }, [isLoggedIn, plusMember, router, user.account, discount_code]);

  useEffect(() => {
    if (resError) {
      captureMessage('Failed to initiate payment', 'fatal');

      fireToast({
        type: 'error',
        message:
          'There was an issue initiating your subscription payment. This issue has now been logged with the Yepic team.',
        id: 'PaymentCheckoutError'
      });
    }
  }, [resError]);

  useEffect(() => {
    setCheckIsUserPlus(plusMember && !hasExpired);
    setNotSSR(true);
  }, [plusMember, hasExpired]);


  return (
    <Loading loading={loading} title='Creating your subscription' message='Loading...'>
      <Layout blankHeader={true} needsLogin={false}>
        <Header title="Plans" />
        <Container>
          <div className={styles.plansWrapper} >
            <h1 className={styles.title}>
              Choose your <LoadIcon name="PlusLogo" className={styles.plusLogo}/> plan
            </h1>
            <p className={styles.description}>
              Subscribe to YEPIC Plus annually for exclusive Founders Club access. Limited spots, available on a first-come, first-served basis.
            </p>
            <Link openNewTab href='https://www.yepic.com/plus'>Find out more about YEPIC Plus</Link>
            <div className={styles.cards}>
              <PlanCard
                title={'Annual'}
                discount={20.88}
                price={'99.00'}
                foundersAccess
                onSelect={() => handlePlanSelection('year')}
                isPlusUser={checkUserIsPlus}
                dominant
              />
              <PlanCard
                title={'Monthly'}
                price={'9.99'}
                onSelect={() => handlePlanSelection('month')}
                isPlusUser={checkUserIsPlus}
              />
            </div>
            {notSSR && isLoggedIn && <>
              { !checkUserIsPlus && <p className={styles.footerNote}>Looking to manage your subscription?</p> }
              <Button type="secondary" href="/profile">Go to profile</Button>
            </>}
          </div>
        </Container>
      </Layout>
    </Loading>
  );
};

export default PlansPage;
